import { I18n } from '@aws-amplify/core';
import { message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { QUERY_KEYS } from '@/utils/query-keys';
import { BeraV2Service } from '@/infra/services/bera-v2';

import type { Response } from '@/types';
import type { BeraClipStatusEvent, BeraSummaryResultV2 } from '@/types/Bera';

import { patchClipStatus } from '../utils';

interface RetryBeraClipParams {
	id: string;
	organization_id: string;
	company_id: string;
	job_summary_id: string;
}

const RETRY_ERROR_MESSAGE = 'Failed to redo the clip';

const Service = BeraV2Service.getInstance();

export function useRetryBeraClip() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: RetryBeraClipParams) => {
			const request = {
				id: params.id,
				organization_id: params.organization_id,
				company_id: params.company_id
			};

			return Service.retryReportClip(request);
		},
		onSuccess: (data, params) => {
			const event: BeraClipStatusEvent = {
				bera_job_summary_id: params.job_summary_id,
				bera_report_id: data.bera_report_id,
				file: data.file
			};

			queryClient.setQueriesData<BeraSummaryResultV2>([QUERY_KEYS.GET_BERA_SUMMARY_RESULT_V2], (summary) =>
				patchClipStatus(summary, event)
			);
		},
		onError: (err: AxiosError<Response>) => {
			message.error(I18n.get(err.response?.data?.message ?? RETRY_ERROR_MESSAGE));
		}
	});
}
